import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import treatments from "../data/treatments";
import procedures from "../data/procedures";

export default function Navbar() {
  const navbarRef = useRef(null);
  const logoRef = useRef(null);
  const treatmentsRef = useRef(null);
  const proceduresRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({
    treatments: false,
    procedures: false
  });

  // Group procedures by category
  const groupedProcedures = procedures.reduce((acc, procedure) => {
    if (!acc[procedure.category]) {
      acc[procedure.category] = [];
    }
    acc[procedure.category].push(procedure);
    return acc;
  }, {});

  // Animation on component mount
  useEffect(() => {
    const tl = gsap.timeline();
    tl.fromTo(
      navbarRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: "power2.out" }
    ).fromTo(
      logoRef.current,
      { scale: 0.9, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.5, ease: "back.out(1.7)" },
      ">"
    );
  }, []);

  const showDropdown = (ref) => {
    gsap.killTweensOf(ref.current);
    gsap.set(ref.current, { display: "block" });
    gsap.fromTo(
      ref.current,
      { y: -10, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.3, ease: "power2.out" }
    );
  };

  const hideDropdown = (ref) => {
    gsap.to(ref.current, {
      y: -10,
      opacity: 0,
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => {
        gsap.set(ref.current, { display: "none" });
      }
    });
  };

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      gsap.to(mobileMenuRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => {
          setMobileMenuOpen(false);
          setMobileDropdownOpen({ treatments: false, procedures: false });
        }
      });
    } else {
      setMobileMenuOpen(true);
      gsap.fromTo(
        mobileMenuRef.current,
        { height: 0, opacity: 0 },
        { height: "auto", opacity: 1, duration: 0.3, ease: "power2.out" }
      );
    }
  };

  const toggleMobileDropdown = (dropdown) => {
    setMobileDropdownOpen(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  return (
    <>
      {/* Add fonts to document head */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Cormorant+Garamond:wght@400;500;600;700&display=swap" 
        rel="stylesheet"
      />
      
      {/* Navbar */}
      <header 
        className="sticky top-0 z-50 bg-teal-50 shadow-sm border-b border-teal-100"
        ref={navbarRef}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 ">
            {/* Logo */}
            <Link to="/" className="h-[90px] overflow-hidden flex items-center -translate-x-5 ">
              <img
                ref={logoRef}
                src="/Images/logo_brown1-removebg-preview.png"
                alt="Clinic Logo"
                className="h-[80%] w-auto transition-opacity hover:opacity-90 "
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              <Link
                to="/"
                className="px-4 py-2 text-teal-800 hover:text-teal-600 hover:bg-teal-100/50 hover:border-b-2 hover:border-teal-800 rounded-lg transition-all font-medium tracking-wide"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
              >
                Home
              </Link>
              
              <Link
                to="/about"
                className="px-4 py-2 text-teal-800 hover:text-teal-600 hover:bg-teal-100/50 hover:border-b-2 hover:border-teal-800 rounded-lg transition-all font-medium tracking-wide"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
              >
                About Us
              </Link>

              {/* Treatments Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => showDropdown(treatmentsRef)}
                onMouseLeave={() => hideDropdown(treatmentsRef)}
              >
                <button 
                  className="px-4 py-2 text-teal-800 hover:text-teal-600 hover:bg-teal-100/50 hover:border-b-2 hover:border-teal-800 rounded-lg transition-all font-medium tracking-wide flex items-center"
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
                >
                  Treatments
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  ref={treatmentsRef}
                  className="absolute left-0 mt-1 w-56 bg-white rounded-lg shadow-lg ring-1 ring-teal-100 z-50 hidden"
                >
                  <div className="py-1">
                    {treatments.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/treatments/${item.slug}`}
                        className="block px-4 py-2.5 text-teal-700 hover:bg-teal-50 hover:border-l-2 hover:border-teal-800 transition-all text-sm tracking-wide"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Procedures Dropdown - Updated with 3 columns and right-aligned */}
              <div
                className="relative"
                onMouseEnter={() => showDropdown(proceduresRef)}
                onMouseLeave={() => hideDropdown(proceduresRef)}
              >
                <button 
                  className="px-4 py-2 text-teal-800 hover:text-teal-600 hover:bg-teal-100/50 hover:border-b-2 hover:border-teal-800 rounded-lg transition-all font-medium tracking-wide flex items-center"
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
                >
                  Procedures
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  ref={proceduresRef}
                  className="absolute -right-1/24 transform -translate-x-1/2 mt-1 w-[600px] bg-white rounded-lg shadow-lg ring-1 ring-teal-100 z-50 hidden p-4"
                >
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(groupedProcedures).map(([category, items]) => (
                      <div key={category} className="space-y-2">
                        <h3 className="text-teal-900 font-semibold text-sm uppercase tracking-wider mb-2 pb-1 border-b border-teal-100">
                          {category}
                        </h3>
                        <div className="space-y-1">
                          {items.map((item) => (
                            <Link
                              key={item.slug}
                              to={`/procedures/${item.slug}`}
                              className="block px-3 py-2 text-teal-700 hover:bg-teal-50 hover:border-l-2 hover:border-teal-800 transition-all text-sm tracking-wide rounded"
                              style={{ fontFamily: "'Montserrat', sans-serif" }}
                            >
                              {item.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/contact"
                className="ml-2 px-5 py-2 bg-teal-600 text-white hover:bg-teal-700 hover:border-2 hover:border-teal-900 rounded-lg transition-all font-medium tracking-wider"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}
              >
                Contact
              </Link>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-teal-800 hover:text-teal-600 hover:bg-teal-100 hover:border-2 hover:border-teal-800 focus:outline-none transition-all"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <div className="w-6 flex flex-col gap-1.5">
                  <span className={`h-0.5 bg-teal-800 transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2 w-6' : 'w-6'}`}></span>
                  <span className={`h-0.5 bg-teal-800 transition-all ${mobileMenuOpen ? 'opacity-0' : 'opacity-100 w-5'}`}></span>
                  <span className={`h-0.5 bg-teal-800 transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2 w-6' : 'w-4'}`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          ref={mobileMenuRef}
          className="md:hidden bg-white shadow-xl"
          style={{ display: mobileMenuOpen ? 'block' : 'none', height: 0, overflow: 'hidden' }}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link
              to="/"
              className="block px-3 py-3 rounded-md text-lg font-medium text-teal-800 hover:text-teal-600 hover:bg-teal-50 hover:border-l-4 hover:border-teal-800 transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            <Link
              to="/about"
              className="block px-3 py-3 rounded-md text-lg font-medium text-teal-800 hover:text-teal-600 hover:bg-teal-50 hover:border-l-4 hover:border-teal-800 transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>

            {/* Mobile Treatments Dropdown */}
            <div>
              <button
                onClick={() => toggleMobileDropdown('treatments')}
                className="w-full flex justify-between items-center px-3 py-3 rounded-md text-lg font-medium text-teal-800 hover:text-teal-600 hover:bg-teal-50 hover:border-l-4 hover:border-teal-800 transition-all"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <span>Treatments</span>
                <svg
                  className={`h-5 w-5 transform transition-transform ${mobileDropdownOpen.treatments ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div
                className={`pl-4 ${mobileDropdownOpen.treatments ? 'block' : 'hidden'}`}
              >
                {treatments.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/treatments/${item.slug}`}
                    className="block px-3 py-2.5 rounded-md text-base font-medium text-teal-700 hover:text-teal-600 hover:bg-teal-50 hover:border-l-2 hover:border-teal-800 transition-all"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile Procedures Dropdown */}
            <div>
              <button
                onClick={() => toggleMobileDropdown('procedures')}
                className="w-full flex justify-between items-center px-3 py-3 rounded-md text-lg font-medium text-teal-800 hover:text-teal-600 hover:bg-teal-50 hover:border-l-4 hover:border-teal-800 transition-all"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <span>Procedures</span>
                <svg
                  className={`h-5 w-5 transform transition-transform ${mobileDropdownOpen.procedures ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div className={`pl-4 ${mobileDropdownOpen.procedures ? 'block' : 'hidden'} max-h-64 overflow-y-auto`}>
                {Object.entries(groupedProcedures).map(([category, items]) => (
                  <div key={category} className="mb-4">
                    <h3 className="text-teal-900 font-medium text-base mt-2 mb-1">
                      {category}
                    </h3>
                    <div className="space-y-1">
                      {items.map((item) => (
                        <Link
                          key={item.slug}
                          to={`/procedures/${item.slug}`}
                          className="block px-3 py-2 rounded-md text-base font-medium text-teal-700 hover:text-teal-600 hover:bg-teal-50 hover:border-l-2 hover:border-teal-800 transition-all"
                          style={{ fontFamily: "'Montserrat', sans-serif" }}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Link
              to="/contact"
              className="block px-3 py-3 rounded-md text-lg font-medium text-white bg-teal-600 hover:bg-teal-700 hover:border-2 hover:border-teal-900 mt-2 transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        </div>
      </header>
    </>
  );
}